import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProfilePreferencesScreen.module.css';
import { apiService } from '../../utils/api';

const INDUSTRIES = [
  'DeFi',
  'AI',
  'Gaming',
  'Infrastructure',
  'Layer 1',
  'Layer 2',
  'Privacy',
  'Social',
  'Metaverse',
  'NFT',
  'DAO',
  'Cross-chain',
  'Oracles',
  'Security',
  'Other'
];

function dedupeCaseInsensitive(items: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of items) {
    const key = item.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      result.push(item);
    }
  }
  return result;
}

function parseInputToArray(input: string): string[] {
  return input
    .split(/[,;\n]/g)
    .map(item => item.trim())
    .filter(Boolean);
}

export const ProfilePreferencesScreen: React.FC = () => {
  const navigate = useNavigate();

  const [buyInput, setBuyInput] = useState('');
  const [sellInput, setSellInput] = useState('');
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buyParsed = useMemo(() => parseInputToArray(buyInput), [buyInput]);
  const sellParsed = useMemo(() => parseInputToArray(sellInput), [sellInput]);

  const toggleIndustry = (name: string) => {
    setSelectedIndustries(prev =>
      prev.includes(name) ? prev.filter(i => i !== name) : [...prev, name]
    );
  };

  const selectAll = () => setSelectedIndustries(INDUSTRIES.slice());
  const clearAll = () => setSelectedIndustries([]);

  const submit = async (payloadOverride?: { buy?: string[]; sell?: string[]; industries?: string[] }) => {
    try {
      setSubmitting(true);
      setError(null);
      const payload = {
        companies_of_interest_buy: dedupeCaseInsensitive(payloadOverride?.buy ?? buyParsed),
        companies_of_interest_sell: dedupeCaseInsensitive(payloadOverride?.sell ?? sellParsed),
        industries_of_interest: dedupeCaseInsensitive(payloadOverride?.industries ?? selectedIndustries)
      };
      await apiService.updateUserProfile(payload);
      navigate('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update profile');
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Investment Preferences</h1>
      <p className={styles.description}>Provide your interests to personalize your experience. You can skip this step.</p>

      <div>
        <div className={styles.sectionTitle}>What assets are you interested in buying?</div>
        <textarea
          className={styles.textarea}
          value={buyInput}
          onChange={e => setBuyInput(e.target.value)}
          placeholder="e.g., Ethereum, Solana, Polygon (comma, semicolon, or new lines)"
          disabled={submitting}
        />
        {buyParsed.length > 0 && (
          <div className={styles.chips}>
            {buyParsed.map(item => (
              <span key={`buy-${item}`} className={styles.chip}>{item}</span>
            ))}
          </div>
        )}
        <div className={styles.hint}>Optional</div>
      </div>

      <div>
        <div className={styles.sectionTitle}>What assets are you interested in selling?</div>
        <textarea
          className={styles.textarea}
          value={sellInput}
          onChange={e => setSellInput(e.target.value)}
          placeholder="e.g., Bitcoin, Cardano (comma, semicolon, or new lines)"
          disabled={submitting}
        />
        {sellParsed.length > 0 && (
          <div className={styles.chips}>
            {sellParsed.map(item => (
              <span key={`sell-${item}`} className={styles.chip}>{item}</span>
            ))}
          </div>
        )}
        <div className={styles.hint}>Optional</div>
      </div>

      <div>
        <div className={styles.sectionTitle}>Which industries interest you?</div>
        <div className={styles.toolbar}>
          <button type="button" className={`${styles.button} ${styles.secondary}`} onClick={selectAll}>Select All</button>
          <button type="button" className={`${styles.button} ${styles.secondary}`} onClick={clearAll}>Clear All</button>
        </div>
        <div className={styles.industriesGrid}>
          {INDUSTRIES.map(ind => (
            <label key={ind} className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={selectedIndustries.includes(ind)}
                onChange={() => toggleIndustry(ind)}
                disabled={submitting}
              />
              <span>{ind}</span>
            </label>
          ))}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.actions}>
        <button type="button" className={`${styles.button} ${styles.secondary}`} disabled={submitting} onClick={() => submit({ buy: [], sell: [], industries: [] })}>Skip</button>
        <div style={{ display: 'flex', gap: 12 }}>
          <button type="button" className={`${styles.button} ${styles.secondary}`} onClick={() => navigate(-1)} disabled={submitting}>Back</button>
          <button type="button" className={styles.button} onClick={() => submit()} disabled={submitting}>{submitting ? 'Saving...' : 'Save & Continue'}</button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePreferencesScreen;
